import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { SearchspringRecommendProvider } from 'libs/searchspring/fetchers/recommend'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'
import clsx from 'clsx'
import { BeaconAnalyticsProvider } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { parseJSON } from '@/components/helpers/utils'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'

// Helper component to track results state
const CarouselResultsWrapper = ({ profile, onResultsChange, children }) => {
  const { results, isLoading } = useSearchspringContext()

  // Notify parent when results/loading state changes
  useEffect(() => {
    onResultsChange(profile, {
      hasResults: results?.results?.length > 0,
      isLoading
    })
  }, [results, isLoading, profile, onResultsChange])

  return <>{children}</>
}

export function CarouselTabbed({ values, itemClass, productId, carouselSettings = {}, productCardSettings, titleContent, ctaContent, limit }) {
  const profiles = useMemo(() => values ? parseJSON(values) : [], [values])
  const parsedSettings = parseJSON(carouselSettings)
  // Handle groupCells value conversion
  if (parsedSettings?.groupCells !== undefined) {
    const value = parsedSettings.groupCells

    // Convert string 'true'/'false' to boolean
    if (value === 'true') {
      parsedSettings.groupCells = true
    } else if (value === 'false') {
      parsedSettings.groupCells = false
    // Convert numeric string to number
    } else if (!isNaN(value) && typeof value === 'string') {
      parsedSettings.groupCells = Number(value)
    }
  }

  const parsedProductCardSettings = parseJSON(productCardSettings)
  const cardSettings = productCardSettings && Object.keys(parsedProductCardSettings).length > 0
    ? parsedProductCardSettings
    : {}

  const [activeProfile, setActiveProfile] = useState(profiles[0]?.profile)
  const [profileStates, setProfileStates] = useState({})

  // Callback to update profile states
  const handleResultsChange = useCallback((profile, state) => {
    setProfileStates(prev => ({
      ...prev,
      [profile]: state
    }))
  }, [])

  // Filter out profiles with no results
  const visibleProfiles = profiles?.filter(profile => {
    const state = profileStates[profile.profile]
    return !state || state.isLoading || state.hasResults
  })

  useEffect(() => {
    if (visibleProfiles.length > 0) {
      const isActiveProfileVisible = visibleProfiles.some(
        profile => profile.profile === activeProfile
      )
      if (!isActiveProfileVisible) {
        setActiveProfile(visibleProfiles[0].profile)
      }
    }
  }, [visibleProfiles, activeProfile])

  return (
    <div className="grid-main gap-x-gutter gap-y-0.25 md:gap-y-0.375 pt-0.25 md:pt-0.375">
      <div className="col-[1/-1] md:col-[1/-3] self-end flex flex-col md:flex-row md:items-center gap-y-0.25 w-fit z-2">
        <div className="empty:hidden sticky left-0 flex items-end md-max:pt-1 pr-1.5" dangerouslySetInnerHTML={{ __html: titleContent }} />
        <div className="md-max:container-full flex items-end gap-x-1.5 overflow-x-scroll no-scrollbar overscroll-x-none whitespace-nowrap">
          {visibleProfiles.map((item) => (
            <button
              className="btn btn-text btn-primary btn-l w-fit"
              key={item.profile}
              onClick={() => setActiveProfile(item.profile)}
              aria-label={item.label}
            >
              <span className={item.profile === activeProfile ? 'text-line text-line-large' : ''}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end col-span-full md:col-[-1/-3] md-max:row-[3] empty:hidden [&_.btn]:md-max:!w-full" dangerouslySetInnerHTML={{ __html: ctaContent }} />
      <div className="container-full col-span-full">
        <div className="relative grid grid-cols-1 grid-rows-1">
          {profiles.map((item) => (
            <SearchspringRecommendProvider
              key={item.profile}
              profile={item.profile}
              productId={productId}
              limit={limit}
            >
              <BeaconAnalyticsProvider profile={item.profile}>
                <CarouselResultsWrapper
                  profile={item.profile}
                  onResultsChange={handleResultsChange}
                >
                  <CarouselResults
                    className={itemClass}
                    data-dl-section-id={item.profile}
                    options={parsedSettings}
                    containerClassName="flex w-full"
                    wrapperClassName={clsx(
                      'transition-opacity duration-150 row-start-1 col-start-1',
                      item.profile !== activeProfile
                        ? 'absolute inset-x-0 top-0 pointer-events-none touch-none opacity-0 delay-150'
                        : 'relative duration-300 z-1 opacity-100'
                    )}
                    productCardSettings={cardSettings}
                  />
                </CarouselResultsWrapper>
              </BeaconAnalyticsProvider>
            </SearchspringRecommendProvider>
          ))}
        </div>
      </div>
    </div>
  )
}
